import type React from "react";

/** Contract every extent picker implements, whatever renderer sits behind it. */
export interface ExtentPickerProps {
  center: { lat: number; lon: number };
  /** Human-readable label for the centre — normally the geocoder's match. */
  address: string;
  radiusM: number;
  onRadiusChange: (metres: number) => void;
  /** Tight height for the floating widget; roomier on the dedicated page. */
  compact?: boolean;
  coverage?: { year: string; collection: string } | null;
  disabled?: boolean;
  /** Transcript copy: no slider, no panning — a record of what was chosen. */
  readOnly?: boolean;
  /** Grown to a tall, near-viewport-width view. The wrapper owns the state. */
  expanded?: boolean;
  /** Line of guidance shown under the expand control and above the map. Owned
   *  by the caller so this module holds none of the product's copy. */
  caption?: React.ReactNode;
  /** Drag-to-reposition. The place name stays; only the coordinates move, and
   *  the extent box travels with them. Omit to pin the marker in place. */
  onCenterChange?: (center: { lat: number; lon: number }) => void;
  /** Present only when expanding is offered; renderers place the toggle at the
   *  head of the info panel, so it sits above the text in both layouts. */
  onToggleExpand?: () => void;
}

/*
 * The floor matches clampRadiusM on the server, which has always raised anything
 * below 50 — a slider offering 25 was showing an area half the size of the one
 * that would actually be reconstructed.
 *
 * RADIUS_STEP_M is 50 so the stops are round numbers and there are twenty of
 * them rather than forty: at 25 they sat about six pixels apart on the rail,
 * which is not a target anyone can hit.
 */
export const RADIUS_MIN_M = 50;
export const RADIUS_MAX_M = 1000;
/*
 * These are half-widths; the box on the ground is twice each of them, which is
 * what the panel shows and what the pipeline reconstructs. So a 25m step is a
 * 50m step in box size, and the 75m default is a 150 x 150 m box.
 */
export const RADIUS_STEP_M = 25;
export const RADIUS_DEFAULT_M = 75;

/** Refits are cheap on GL, costly on raster tiles — never fire one per slider tick. */
export const FIT_DEBOUNCE_MS = 180;

/** Beat on the wide view before descending, so the user registers where they are. */
export const INTRO_HOLD_MS = 700;
/** Descent duration. Slow enough to read the surrounding context on the way down. */
export const INTRO_FLIGHT_MS = 4500;

export type Bounds = [[number, number], [number, number]];

/**
 * Half-width in metres → the square bbox actually sent to the pipeline.
 * Returned as [[southLat, westLon], [northLat, eastLon]] to match Leaflet;
 * the Mapbox picker swaps to lon/lat at the call site.
 */
export function extentBounds(lat: number, lon: number, radiusM: number): Bounds {
  const latDelta = radiusM / 111320;
  const lonDelta = radiusM / (111320 * Math.cos((lat * Math.PI) / 180));
  return [
    [lat - latDelta, lon - lonDelta],
    [lat + latDelta, lon + lonDelta],
  ];
}
